import React from 'react';
import { Grid } from '@mui/material';
import { useTranslation, Trans } from '@gamiphy/react-i18n';
import Profile from '@gamiphy/icons/Profile';
import Share from '@gamiphy/icons/Share';
import TwoProfiles from '@gamiphy/icons/TwoProfiles';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';
import { TierConfigType } from '@gamiphy/bl/level';
import { ReferralPointsDto } from '@coretava/api/events-service';

type Props = {
  referrerPoints?: ReferralPointsDto,
  refereePoints?: ReferralPointsDto,
}
const InviteSteps: React.FC<Props> = ({ referrerPoints, refereePoints }) => {
  const { t } = useTranslation();
  const { tierConfig } = LevelStore;
  const conditions = React.useMemo(() => {
    const referrerBody = !!referrerPoints?.invite.gems || !!referrerPoints?.invite.exp || !!referrerPoints?.purchase.gems || !!referrerPoints?.purchase.exp;
    const inviteBody = !!referrerPoints?.invite.gems || (!!referrerPoints?.invite.exp && tierConfig?.type === TierConfigType.exp);
    const inviteExpBody = !!referrerPoints?.invite.gems && !!referrerPoints?.invite.exp && tierConfig?.type === TierConfigType.exp;
    const purchaseExpBody = !!referrerPoints?.purchase.gems && !!referrerPoints?.purchase.exp && tierConfig?.type === TierConfigType.exp;
    const refereeBody = !!refereePoints?.invite.gems || !!refereePoints?.invite.exp || !!refereePoints?.purchase.gems || !!refereePoints?.purchase.exp;
    const refereeSignUPBody = !!refereePoints?.invite.gems || (!!refereePoints?.invite.exp && tierConfig?.type === TierConfigType.exp);
    const refereeExpBody = !!refereePoints?.invite.gems && !!refereePoints?.invite.exp && tierConfig?.type === TierConfigType.exp;
    const refereePurchaseWithInvite = !!refereePoints?.invite.gems || (!!refereePoints?.invite.exp && tierConfig?.type === TierConfigType.exp);
    return {
      referrerBody,
      inviteBody,
      inviteExpBody,
      purchaseExpBody,
      refereeBody,
      refereeExpBody,
      refereeSignUPBody,
      refereePurchaseWithInvite,
    };
  }, [refereePoints, referrerPoints, tierConfig]);

  return (
    <>
      <Grid container alignItems="center" columnSpacing={1} size={12} wrap="nowrap">
        <Grid>
          <Share color={ThemeStore.theme.palette?.primary?.main} />
        </Grid>
        <Grid sx={{ fontSize: '1.3rem', fontWeight: 400 }}>
          {t('referral.tab.stepOne', 'Invite your friend to sign-up and make a purchase')}
        </Grid>
      </Grid>
      {(conditions.referrerBody) && (
      <Grid container direction="row" alignItems="center" wrap="nowrap" spacing={1}>
        <Grid>
          <Profile color={ThemeStore.theme?.palette?.primary?.main} />
        </Grid>
        <Grid sx={{ fontSize: '1.3rem', fontWeight: 400 }}>
          <Trans
            i18nKey="referral.tab.yourReward.prefix"
            defaults="You earn"
          />
          {(conditions.inviteBody) && (
            <>
              {!!referrerPoints?.invite.gems && (
              <Trans
                i18nKey="referral.tab.reward.gems"
                defaults=" <bold>{{gems, gems}} </bold> "
                values={{
                  gems: referrerPoints?.invite.gems,
                }}
                components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
              />
              )}
              {(conditions.inviteExpBody) && (
              <Trans
                i18nKey="referral.tab.reward.and.postfix"
                defaults="and"
              />
              )}
              {!!referrerPoints?.invite.exp && tierConfig?.type === TierConfigType.exp && (
              <Trans
                i18nKey="referral.tab.reward.exp"
                defaults="<bold>{{exp, exp}} </bold>"
                values={{
                  exp: referrerPoints?.invite.exp,
                }}
                components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
              />
              )}
              <Trans
                i18nKey="referral.tab.yourReward.signup"
                defaults="upon Signing up"
              />
              {(!!referrerPoints?.purchase.gems || !!referrerPoints?.purchase.exp) && (
              <Trans
                i18nKey="referral.tab.reward.andAnother.postfix"
                defaults="and another"
              />
              )}
            </>
          )}
          {(!!referrerPoints?.purchase.gems || !!referrerPoints?.purchase.exp) && (
            <>
              <>
                {!!referrerPoints?.purchase.gems && (
                <Trans
                  i18nKey="referral.tab.reward.gems"
                  defaults=" <bold>{{gems, gems}} </bold> "
                  values={{
                    gems: referrerPoints?.purchase.gems,
                  }}
                  components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
                />
                )}
                {(conditions.purchaseExpBody) && (
                <Trans
                  i18nKey="referral.tab.reward.and.postfix"
                  defaults="and"
                />
                )}
                {!!referrerPoints?.purchase.exp && tierConfig?.type === TierConfigType.exp && (
                <Trans
                  i18nKey="referral.tab.reward.exp"
                  defaults="<bold>{{exp, exp}} </bold>"
                  values={{
                    exp: referrerPoints?.purchase.exp,
                  }}
                  components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
                />
                )}
              </>
              <Trans
                i18nKey="referral.tab.yourReward.purchase"
                defaults="when your friends make a purchase"
              />
            </>
          )}
        </Grid>
      </Grid>
      )}
      {(conditions.refereeBody) && (
      <Grid container direction="row" alignItems="center" wrap="nowrap" spacing={1}>
        <Grid>
          <TwoProfiles color={ThemeStore.theme?.palette?.primary?.main} />
        </Grid>
        <Grid sx={{ fontSize: '1.3rem', fontWeight: 400 }}>
          {(conditions.refereeSignUPBody) && (
            <>
              <Trans
                i18nKey="referral.tab.yourReward.referee.signup"
                defaults="Upon Signing up , they receive"
              />
              {!!refereePoints?.invite.gems && (
              <Trans
                i18nKey="referral.preview.reward.gems"
                defaults=" <bold>{{gems, gems}} </bold> "
                values={{
                  gems: refereePoints?.invite.gems,
                }}
                components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
              />
              )}
              {(conditions.refereeExpBody) && (
              <Trans
                i18nKey="referral.tab.reward.and.postfix"
                defaults="and"
              />
              )}
              {!!refereePoints?.invite.exp && tierConfig?.type === TierConfigType.exp && (
              <Trans
                i18nKey="referral.tab.reward.exp"
                defaults="<bold>{{exp, exp}} </bold>"
                values={{
                  exp: refereePoints?.invite.exp,
                }}
                components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
              />
              )}
            </>
          )}
          {(!!refereePoints?.purchase.gems || (!!refereePoints?.purchase.exp && tierConfig?.type === TierConfigType.exp)) && (
          <>
            {(conditions.refereePurchaseWithInvite) && (
            <Trans
              i18nKey="referral.tab.reward.referee.purchase"
              defaults=" ,and when they make a purchase "
            />
            )}
            {(!conditions.refereePurchaseWithInvite) && (
            <Trans
              i18nKey="referral.tab.reward.referee.purchase.with.invite"
              defaults="When they make a purchase "
            />
            )}
            <Trans
              i18nKey="referral.tab.reward.referee.purchase.earn"
              defaults=",They also earn "
            />
            {!!refereePoints?.purchase.gems && (
            <Trans
              i18nKey="referral.preview.reward.gems"
              defaults=" <bold>{{gems, gems}} </bold> "
              values={{
                gems: refereePoints?.purchase.gems,
              }}
              components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
            />
            )}
            {!!refereePoints?.purchase.exp && tierConfig?.type === TierConfigType.exp && (
            <Trans
              i18nKey="referral.tab.reward.exp"
              defaults=" and <bold>{{exp, exp}} </bold> Exp"
              values={{
                exp: refereePoints?.purchase.exp,
              }}
              components={{ bold: <span style={{ fontWeight: 'bold' }} /> }}
            />
            )}
          </>
          )}
        </Grid>
      </Grid>
      )}
    </>
  );
};

export default InviteSteps;
